import { ClientBetsType, IClientBet } from 'widgets/Roulette/types';
import { call, put, select } from 'redux-saga/effects';
import { arrayToBets, sumBets } from 'widgets/Roulette/helpers';
import { clearAllBetsAction, refreshBetsAction } from '../actions';
import { BetsTypes, raceTrackBets } from 'widgets/Roulette/const';
import { pick } from 'ramda';
import { IRoundState, RoundStates } from '../../RoundStates/types';
import { roundStateSelector } from '../../RoundStates';
import { ICreatePendingBetRequestData, RestService } from 'services/RestAPIService';
import { setTotalBetAction } from '../../User/actions';
import * as Sentry from '@sentry/react';
import { totalBetSelector } from '../../User';

type RefreshBetsType = {
  past: [] | ClientBetsType[];
  present: IClientBet[];
  future: [] | ClientBetsType[];
};

export type ServerBetsType = {
  bets: IClientBet[];
  info: IClientBet[];
};

export function* refreshBetsSaga({ past, present, future }: RefreshBetsType) {
  const reduxBetsWithUpdatedConfirm = present.map((bet: IClientBet) => {
    const { bet: currentBet = 0, confirmed = 0 } = bet;
    const realConfirmed = confirmed > currentBet ? currentBet : confirmed;
    return { ...bet, confirmed: realConfirmed };
  });

  const updatedPresent = arrayToBets(reduxBetsWithUpdatedConfirm);

  yield put(refreshBetsAction({ past, present: updatedPresent, future }));

  yield call(sendBetsToServer, present);
}

export function* sendBetsToServer(clientBets: IClientBet[]) {
  const baseProps = ['type', 'bet', 'selector'];

  const betsForServer: ServerBetsType = clientBets.reduce(
    (acc: ServerBetsType, clientBet: IClientBet) => {
      const { type, bet, label } = clientBet;

      if (!bet) return acc;

      // @ts-ignore
      const modifiedBet = pick(baseProps, clientBet);

      const isColumnOrDozen = type === BetsTypes.column || type === BetsTypes.dozen;

      const serverBet = isColumnOrDozen
        ? { ...modifiedBet, item: Number(label?.slice(0, 1)) }
        : modifiedBet;

      const isInfoBet =
        raceTrackBets.has(type) ||
        type === BetsTypes.historyStraight ||
        type === BetsTypes.hotColdStraight;

      return isInfoBet
        ? { ...acc, info: [...acc.info, modifiedBet] }
        : { ...acc, bets: [...acc.bets, serverBet] };
    },
    { bets: [], info: [] },
  );

  yield call(pushBets, betsForServer);
}

export function* pushBets({ bets, info }: ServerBetsType) {
  const roundState: IRoundState['roundState'] = yield select(roundStateSelector);

  if (roundState === RoundStates.BettingTimeStarted) {
    const data: ICreatePendingBetRequestData = {
      bets,
      info,
      timeStamp: new Date().getTime(),
    };

    try {
      const response: ICreatePendingBetRequestData = yield call(() =>
        RestService.createPendingBet(data),
      );

      const totalBet = sumBets(bets);
      yield put(setTotalBetAction(totalBet));
    } catch (error) {
      Sentry.captureException({ error, data });
      const totalBet: number | null = yield select(totalBetSelector);

      if (!totalBet) {
        yield put(clearAllBetsAction());
      }

      console.log(error);
    }
  }
}
